import {
  Suspense,
  useCallback,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";

import {
  ArrowRightIcon,
  Chip,
  EyeIcon,
  PencilSimpleIcon,
  ProgressBar,
  UserIcon,
} from "@stacklet/ui";
import { graphql, useLazyLoadQuery } from "react-relay";
import { generatePath } from "react-router";

import { Link } from "app/components/links";
import { RbacChip } from "app/components/rbac/RbacChip";
import { RoleAssignmentTable } from "app/components/rbac/RoleAssignmentTable";
import { UserBrowser } from "app/components/rbac/UserBrowser";
import {
  PaginationFooter,
  StackletGrid,
  type StackletGridPagination,
} from "app/components/table/StackletGrid";
import { SEARCH_DEBOUNCE_MS } from "app/constants";
import { paths } from "app/constants/paths";
import { useHasScopePermission } from "app/hooks";
import {
  firstPage,
  useDebouncedCallback,
  useSectionPagination,
  type SectionPage,
} from "app/hooks/ui";

import {
  buildFilter,
  type GroupBand,
  type GroupRow,
  type GroupsView,
} from "./groupBands";
import { BandCell, GroupNameCell } from "./GroupBandsGridCells";

import type { GroupBandsGridGroupsQuery } from "./__generated__/GroupBandsGridGroupsQuery.graphql";
import type { GridColDef } from "@mui/x-data-grid-pro";

export type { GroupBand, GroupsView } from "./groupBands";

/** Visibility-scope chips shown per row before the rest collapse to "+N". */
const MAX_VISIBILITY_CHIPS = 2;
/** Number of leaf columns; band, panel and pager rows span them all. */
const COLUMN_COUNT = 4;
/**
 * Kept in sync with the `roleAssignments(first:)` cap in the query below; used
 * to infer truncation when the connection reports no `pageInfo.total`.
 */
const ROLE_ASSIGNMENTS_PAGE_SIZE = 100;

// Compact cell padding and a short header row with small secondary labels.
const GRID_CLASS_NAMES = [
  "[&_.MuiDataGrid-cell]:!px-xs",
  "[&_.MuiDataGrid-columnHeader]:!px-xs",
  "[&_.MuiDataGrid-columnHeader]:!text-text-secondary",
  "[&_.MuiDataGrid-columnHeaderTitle]:!text-label-small",
  // Zero the panel cell's own padding so its background fills edge to edge (its
  // inner px-lg still insets content). Direct child (`>`) only, else it'd strip
  // padding from the nested UserBrowser grid's cells too.
  "[&_.panel-row>.MuiDataGrid-cell]:!p-0",
].join(" ");

/** A group connection page's cursor flags. */
interface BandPageInfo {
  hasNextPage: boolean;
  hasPreviousPage: boolean;
  startCursor: string | null;
  endCursor: string | null;
  total: number | null;
}

/** What a loaded band holds: its current group page and that page's cursors. */
interface BandGroupsData {
  groups: GroupRow[];
  pageInfo: BandPageInfo;
}

/** A band header row. */
interface BandRow {
  kind: "band";
  id: string;
  band: GroupBand;
  /** Whether the band's groups are loading (drives the header's loading bar). */
  isLoading: boolean;
}

/** A group row beneath a band. */
interface GroupItemRow {
  kind: "group";
  id: string;
  band: GroupBand;
  group: GroupRow;
  /** Whether this group's detail panel is open (drives the caret). */
  isExpanded: boolean;
}

/** A group's expanded detail panel row. */
interface PanelRow {
  kind: "panel";
  id: string;
  group: GroupRow;
}

/** A band's pagination footer row. */
interface PagerRow {
  kind: "pager";
  id: string;
  band: GroupBand;
  pagination: StackletGridPagination;
}

/** A grid row: a band header, a group, an expanded panel, or a band's pager. */
type Row = BandRow | GroupItemRow | PanelRow | PagerRow;

/** The loaded group node a {@link GroupRow} is denormalised from. */
type LoadedGroupNode = GroupRow["principalRef"];

/** Denormalises a loaded group node into a group row. Colocated with the query
 * whose fields it reads. */
function groupRowFromNode(node: LoadedGroupNode): GroupRow {
  const assignments = (node.roleAssignments.edges ?? []).map(
    (edge) => edge.node,
  );

  // pageInfo.total is best-effort (nullable); fall back to inferring truncation
  // from a full page of fetched edges.
  const total = node.roleAssignments.pageInfo.total;
  const hasMoreAssignments =
    total != null
      ? total > assignments.length
      : assignments.length >= ROLE_ASSIGNMENTS_PAGE_SIZE;

  return {
    id: node.id,
    uuid: node.uuid,
    name: node.name,
    label: node.displayName || node.name,
    memberCount: node.members.pageInfo.total ?? null,
    hasAdmin: assignments.some(({ role }) => role.name === "admin"),
    visibilityTargets: assignments
      .filter(({ role }) => role.name === "resource-viewer")
      .map(({ target }) =>
        target.__typename === "AccountGroup" ? target.name : "",
      )
      .filter(Boolean),
    hasMoreAssignments,
    principalRef: node,
  };
}

interface GroupsLoaderProps {
  /** The band whose groups to load. */
  band: GroupBand;
  /** The current group page's cursor window. */
  page: SectionPage;
  /** Active search term, matched against group name. */
  search: string;
  /** Called with the loaded groups. */
  onLoaded: (bandId: string, data: BandGroupsData) => void;
}

/** Loads one band's current group page and reports it up; renders nothing. */
function GroupsLoader({ band, page, search, onLoaded }: GroupsLoaderProps) {
  const data = useLazyLoadQuery<GroupBandsGridGroupsQuery>(
    graphql`
      query GroupBandsGridGroupsQuery(
        $filterElement: FilterElementInput
        $first: Int
        $last: Int
        $after: String
        $before: String
      ) {
        userGroups(
          filterElement: $filterElement
          first: $first
          last: $last
          after: $after
          before: $before
          order: "name"
        ) {
          edges {
            node {
              displayName
              id
              members(first: 1) {
                pageInfo {
                  total
                }
              }
              name
              roleAssignments(first: 100) {
                edges {
                  node {
                    id
                    role {
                      name
                    }
                    target {
                      __typename
                      ... on AccountGroup {
                        name
                      }
                    }
                  }
                }
                pageInfo {
                  total
                }
              }
              ...RoleAssignmentTable_principal
              uuid
            }
          }
          pageInfo {
            endCursor
            hasNextPage
            hasPreviousPage
            startCursor
            total
          }
        }
      }
    `,
    {
      filterElement: buildFilter(band.scope, search),
      first: page.first,
      last: page.last,
      after: page.after,
      before: page.before,
    },
  );

  const result = useMemo<BandGroupsData>(
    () => ({
      groups: (data.userGroups.edges ?? [])
        .map((edge) => edge.node)
        .filter((node): node is NonNullable<typeof node> => Boolean(node))
        .map(groupRowFromNode),
      pageInfo: {
        hasNextPage: data.userGroups.pageInfo.hasNextPage,
        hasPreviousPage: data.userGroups.pageInfo.hasPreviousPage,
        startCursor: data.userGroups.pageInfo.startCursor ?? null,
        endCursor: data.userGroups.pageInfo.endCursor ?? null,
        total: data.userGroups.pageInfo.total ?? null,
      },
    }),
    [data],
  );

  useEffect(() => {
    onLoaded(band.id, result);
  }, [band.id, result, onLoaded]);
  return null;
}

interface Props {
  /** The provider bands to list, one section each. */
  bands: GroupBand[];
  /** What an expanded group row shows: its role assignments or its members. */
  view: GroupsView;
  /** Whether the band list is still resolving upstream. */
  loading?: boolean;
  /** Singular noun for the empty state, e.g. "provider". */
  unit?: string;
  /** Toolbar content before the Columns button — the view toggle / filter. */
  toolbarActions?: ReactNode;
  /** Storage key for the grid's sticky column state. */
  tableId: string;
}

/**
 * Every identity provider's groups and the role assignments each group holds,
 * grouped into per-provider bands within one aligned grid. Expanding a group
 * shows either its full role assignments (the shared {@link RoleAssignmentTable})
 * or its members (the shared {@link UserBrowser}), per the {@link GroupsView}
 * toggle. Each band loads its groups on its own, scoped by the provider's
 * `idp-namespace`, so the filter that chooses which bands appear is applied
 * server-side.
 */
export function GroupBandsGrid({
  bands,
  view,
  loading,
  unit = "provider",
  toolbarActions,
  tableId,
}: Props) {
  const hasPermission = useHasScopePermission();
  const canManageGroups = hasPermission("system", "EDIT");

  const [expandedGroups, setExpandedGroups] = useState<ReadonlySet<string>>(
    new Set(),
  );

  const toggleGroup = useCallback((id: string) => {
    setExpandedGroups((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  }, []);

  // Per-band cursor pagination for the groups under each band. Bands are always
  // open, so `expanded`/`toggle` go unused (the group-panel toggle is separate).
  const { pageById, dataById, loadingById, setPageSize, onLoaded, getPager } =
    useSectionPagination<BandGroupsData>();

  // `search` is the live field value; `appliedSearch` is the term the loaded
  // groups were fetched with. Debounced so a keystroke doesn't refetch every
  // band on each character.
  const [search, setSearch] = useState("");
  const [appliedSearch, setAppliedSearch] = useState("");
  const applySearch = useDebouncedCallback((value: string) => {
    setAppliedSearch(value);
    // A new filter invalidates each band's cursors, so send every band back to
    // its first page. Flagging them loading also shows each band's bar (and
    // disables its pager) until the refetched page lands.
    for (const band of bands) {
      setPageSize(band.id, pageById[band.id]?.pageSize ?? firstPage().pageSize);
    }
  }, SEARCH_DEBOUNCE_MS);
  const handleSearchChange = (value: string) => {
    setSearch(value);
    applySearch.run(value);
  };

  const rows: Row[] = useMemo(
    () =>
      bands.flatMap((band) => {
        const section = dataById[band.id];
        const bandRow: BandRow = {
          kind: "band",
          id: `band:${band.id}`,
          band,
          isLoading: !section || Boolean(loadingById[band.id]),
        };
        if (!section) return [bandRow];

        const groupRows: Row[] = section.groups.flatMap((group) => {
          const isExpanded = expandedGroups.has(group.id);
          const groupRow: GroupItemRow = {
            kind: "group",
            id: `${band.id}:${group.id}`,
            band,
            group,
            isExpanded,
          };
          return isExpanded
            ? [
                groupRow,
                {
                  kind: "panel" as const,
                  id: `panel:${band.id}:${group.id}`,
                  group,
                },
              ]
            : [groupRow];
        });

        const pager = getPager(band.id, {
          pageInfo: section.pageInfo,
          rowCount: section.groups.length,
          unit: "group",
        });
        if (!pager) return [bandRow, ...groupRows];

        return [
          bandRow,
          ...groupRows,
          {
            kind: "pager" as const,
            id: `pager:${band.id}`,
            band,
            pagination: pager,
          },
        ];
      }),
    [bands, dataById, loadingById, expandedGroups, getPager],
  );

  const columns: GridColDef<Row>[] = useMemo(
    () => [
      {
        field: "label",
        headerName: "Name",
        flex: 1,
        minWidth: 260,
        sortable: false,
        colSpan: (params) => (params.row.kind === "group" ? 1 : COLUMN_COUNT),
        renderCell: ({ row }) => {
          if (row.kind === "band") {
            return <BandCell band={row.band} isLoading={row.isLoading} />;
          }
          if (row.kind === "pager") {
            return (
              <div className="w-full">
                <PaginationFooter {...row.pagination} />
              </div>
            );
          }
          if (row.kind === "panel") {
            return <PanelCell group={row.group} view={view} />;
          }
          return (
            <GroupNameCell
              band={row.band}
              group={row.group}
              isExpanded={row.isExpanded}
              onToggle={() => toggleGroup(row.group.id)}
            />
          );
        },
      },
      {
        // At-a-glance access: resource-viewer scope chips and an Admin badge.
        // The expanded panel has the full role-assignment picture.
        field: "access",
        headerName: "",
        flex: 1,
        minWidth: 200,
        sortable: false,
        renderCell: ({ row }) => {
          if (row.kind !== "group") return null;
          const shown = row.group.visibilityTargets.slice(
            0,
            MAX_VISIBILITY_CHIPS,
          );
          const overflow = row.group.visibilityTargets.length - shown.length;

          return (
            <div className="flex h-full items-center justify-end gap-xs">
              {shown.length > 0 ? (
                <EyeIcon
                  aria-label="Visibility scope"
                  className="size-4 shrink-0 text-text-secondary"
                />
              ) : null}
              {shown.map((target) => (
                <RbacChip
                  key={target}
                  label={target}
                  rbacType="column-account-group"
                  variant="scope"
                />
              ))}
              {overflow > 0 ? (
                <span className="text-label-small text-text-secondary">
                  +{overflow}
                </span>
              ) : null}
              {row.group.hasAdmin ? <Chip color="dark-grey">Admin</Chip> : null}
              {/*
                The summary is computed from the first page of assignments, so
                say it is incomplete rather than presenting it as the whole
                picture (e.g. an admin grant beyond the page would be missing).
              */}
              {row.group.hasMoreAssignments ? (
                <span className="text-label-small text-text-secondary">
                  + more
                </span>
              ) : null}
            </div>
          );
        },
      },
      {
        field: "memberCount",
        headerName: "# Members",
        width: 130,
        sortable: false,
        renderCell: ({ row }) =>
          row.kind === "group" ? (
            <div className="flex h-full items-center gap-xs">
              <UserIcon
                aria-label={`${row.group.label} members`}
                className="size-4 shrink-0 text-text-secondary"
              />
              <span>{row.group.memberCount ?? "--"}</span>
            </div>
          ) : null,
      },
      {
        field: "actions",
        headerName: "",
        width: 88,
        sortable: false,
        renderCell: ({ row }) => {
          if (row.kind !== "group") return null;
          return (
            // Flex (not MUI `align`) with the band header's gap so the two
            // rows' edit/arrow align; the buttons' p-md gives the hit area.
            <div className="flex h-full w-full items-center justify-end gap-xxs">
              {canManageGroups ? (
                <Link
                  className="inline-flex p-md text-text-secondary hover:text-text-primary"
                  textDecoration="none"
                  to={generatePath(paths.groupEdit, {
                    groupUuid: row.group.uuid,
                  })}
                >
                  <PencilSimpleIcon
                    aria-label={`Edit ${row.group.label}`}
                    className="size-4"
                  />
                </Link>
              ) : null}
              <Link
                className="inline-flex p-md text-text-secondary hover:text-text-primary"
                textDecoration="none"
                to={generatePath(paths.groupDetails, {
                  groupUuid: row.group.uuid,
                })}
              >
                <ArrowRightIcon
                  aria-label={`View ${row.group.label}`}
                  size={16}
                />
              </Link>
            </div>
          );
        },
      },
    ],
    // Keep `columns` stable across expand — cells read their state off `row`,
    // not closures — or the grid re-autosizes and the columns visibly re-fit.
    [view, canManageGroups, toggleGroup],
  );

  return (
    <>
      {/* One loader per band fills its current group page; paging or a search
          term refetches it. */}
      {bands.map((band) => (
        <Suspense fallback={null} key={band.id}>
          <GroupsLoader
            band={band}
            onLoaded={onLoaded}
            page={pageById[band.id] ?? firstPage()}
            search={appliedSearch}
          />
        </Suspense>
      ))}

      <StackletGrid
        className={GRID_CLASS_NAMES}
        columnHeaderHeight={36}
        columns={columns}
        defaultDensity="comfortable"
        getRowClassName={(params) => {
          if (params.row.kind === "band") return "bg-background-secondary";
          if (params.row.kind === "panel") return "panel-row";
          return "";
        }}
        getRowHeight={(params) =>
          (params.model as Row).kind === "panel" ? "auto" : 52
        }
        getRowId={(row) => row.id}
        loading={loading}
        onSearchChange={handleSearchChange}
        rows={rows}
        search={search}
        searchAriaLabel="Search groups"
        searchPlaceholder="Search groups"
        slots={{
          noRowsOverlay: () => (
            <div className="flex h-full items-center justify-center text-label-small text-text-secondary">
              {appliedSearch.trim()
                ? "No groups match your search"
                : `No ${unit}s`}
            </div>
          ),
        }}
        tableId={tableId}
        toolbarActions={toolbarActions}
        exportTrailing
        hideDensityButton
        transparentSearch
      />
    </>
  );
}

interface PanelCellProps {
  /** The group whose detail to show. */
  group: GroupRow;
  /** Whether to show the group's role assignments or its members. */
  view: GroupsView;
}

/** A group's expanded detail: its role assignments, or its members. Colocated
 * with the query that supplies its `...RoleAssignmentTable_principal` data. */
function PanelCell({ group, view }: PanelCellProps) {
  return (
    <div className="w-full border-b border-overlay-medium bg-background-primary px-lg py-sm">
      {view === "users" ? (
        // Search stays empty — the grid above searches groups, not members.
        <Suspense fallback={<ProgressBar size="slim" indeterminate />}>
          <UserBrowser
            enableExport={false}
            scope={{ kind: "group-uuid", groupUuid: group.uuid }}
            searchTerm=""
            showSsoOrigin={false}
            // One shared id, not one per group: the id keys per-table
            // localStorage, so a per-group id would leave an entry behind for
            // every group ever expanded.
            tableId="settings-groups-group-users"
            title={null}
            fixedColumnWidths
          />
        </Suspense>
      ) : (
        <>
          <RoleAssignmentTable
            principalRef={group.principalRef}
            showEmptyRoles
          />
          {/* The table shows the first page of assignments; flag that more
           * exist rather than implying the list is complete. */}
          {group.hasMoreAssignments ? (
            <p className="pb-sm text-label-small text-text-secondary">
              Only the first {ROLE_ASSIGNMENTS_PAGE_SIZE} role assignments are
              shown.
            </p>
          ) : null}
        </>
      )}
    </div>
  );
}
